import React from 'react'
import { createChart, type IChartApi, type LineData } from 'lightweight-charts'
import { Card, CardContent, CardHeader } from '@/shared/ui'
import type { RewardsBingxDailyItem } from '../services/rewardsBingxService'
import { calcDiscountBrlFromCommissionUsdt } from '../utils/rewardsMath'
import { formatCurrency, formatUsdt } from '../utils/rewardsFormat'

export default function RewardsBingxDailyChartCard({
  items,
  multiplierX,
  usdtBrlRate,
}: {
  items: RewardsBingxDailyItem[]
  multiplierX: number
  usdtBrlRate: number
}) {
  const containerRef = React.useRef<HTMLDivElement | null>(null)
  const chartRef = React.useRef<IChartApi | null>(null)
  const seriesRef = React.useRef<ReturnType<IChartApi['addLineSeries']> | null>(null)

  const points = React.useMemo<LineData[]>(() => {
    const sorted = Array.isArray(items) ? [...items].sort((a, b) => a.time_ms - b.time_ms) : []
    let commissionCumUsdt = 0
    const out: LineData[] = []
    for (const it of sorted) {
      commissionCumUsdt += Number(it.commission_usdt || 0)
      const discount = calcDiscountBrlFromCommissionUsdt({
        commissionConfirmedUsdt: commissionCumUsdt,
        multiplierX,
        usdtBrlRate,
      })
      const t = Math.floor(Number(it.time_ms || 0) / 1000)
      if (!Number.isFinite(t) || t <= 0) continue
      out.push({ time: t as any, value: discount })
    }
    return out
  }, [items, multiplierX, usdtBrlRate])

  const totals = React.useMemo(() => {
    let tradingVolumeUsdt = 0
    let commissionTotalUsdt = 0
    for (const it of items || []) {
      tradingVolumeUsdt += Number(it.trading_volume_usdt || 0)
      commissionTotalUsdt += Number(it.commission_usdt || 0)
    }
    const discountBrl = calcDiscountBrlFromCommissionUsdt({
      commissionConfirmedUsdt: commissionTotalUsdt,
      multiplierX,
      usdtBrlRate,
    })
    return { tradingVolumeUsdt, discountBrl }
  }, [items, multiplierX, usdtBrlRate])

  React.useEffect(() => {
    const el = containerRef.current
    if (!el || chartRef.current) return

    const chart = createChart(el, {
      layout: { background: { color: 'transparent' }, textColor: 'rgba(148,163,184,0.85)' },
      rightPriceScale: { borderColor: 'transparent' },
      timeScale: { borderColor: 'transparent', timeVisible: true },
      grid: { vertLines: { visible: false }, horzLines: { visible: false } },
      crosshair: { mode: 0 },
      handleScroll: { mouseWheel: false, pressedMouseMove: false, horzTouchDrag: false, vertTouchDrag: false } as any,
      handleScale: { mouseWheel: false, pinch: false } as any,
      autoSize: true,
    })
    const series = chart.addLineSeries({ color: '#22c55e', lineWidth: 2, priceLineVisible: false })

    chartRef.current = chart
    seriesRef.current = series

    const ro = new ResizeObserver(() => {
      try {
        chart.applyOptions({ autoSize: true })
      } catch {}
    })
    ro.observe(el)

    return () => {
      try {
        ro.disconnect()
      } catch {}
      try {
        chart.remove()
      } catch {}
      chartRef.current = null
      seriesRef.current = null
    }
  }, [])

  React.useEffect(() => {
    if (!seriesRef.current) return
    seriesRef.current.setData(points)
    try {
      chartRef.current?.timeScale().fitContent()
    } catch {}
  }, [points])

  if (!items || items.length === 0) {
    return (
      <Card className="border-0 shadow">
        <CardHeader className="border-b-0 text-base font-semibold">BingX - Gráfico de desconto</CardHeader>
        <CardContent className="text-sm text-foreground/70">Sem dados diários suficientes para exibir o gráfico ainda.</CardContent>
      </Card>
    )
  }

  return (
    <Card className="border-0 shadow">
      <CardHeader className="border-b-0 text-base font-semibold">BingX - Gráfico de desconto (ciclo)</CardHeader>
      <CardContent className="space-y-3">
        <div className="grid gap-3 md:grid-cols-2">
          <div className="rounded-lg bg-muted/30 p-4">
            <div className="text-xs text-foreground/60">Volume no ciclo</div>
            <div className="mt-1 text-lg font-semibold">{formatUsdt(totals.tradingVolumeUsdt)} USDT</div>
          </div>
          <div className="rounded-lg bg-muted/30 p-4">
            <div className="text-xs text-foreground/60">Desconto estimado</div>
            <div className="mt-1 text-lg font-semibold">{usdtBrlRate > 0 ? formatCurrency(totals.discountBrl, 'BRL') : '-'}</div>
          </div>
        </div>

        {usdtBrlRate <= 0 && (
          <div className="rounded-md bg-amber-500/10 p-2 text-xs text-amber-400">
            Taxa USDT→BRL ainda não sincronizada. O gráfico fica indisponível até atualizar.
          </div>
        )}

        {usdtBrlRate > 0 && <div ref={containerRef} className="h-48 w-full overflow-hidden rounded-lg bg-muted/20" />}
      </CardContent>
    </Card>
  )
}

